// The Core Orientation tab's table: every approved CoreTrak shot on a hole, one
// row each, in depth order. CoreTrak takes one shot per run, so this is the
// hole's core runs read as one list.
//
// Built from POST api/surveys/composite (SurveyPositions.BuildComposite), which
// already joins the runs, drops rejected shots and non-CoreTrak surveys, and
// tags each shot with the survey it came from - so a row can link back to it.

import { orderedPositions } from './surveyPositions';

// The composite run's positions with the collar (0, 0, 0) in front. Positions
// are measured from the collar, but the first shot is taken some metres down
// the hole, so without the origin the line starts in mid-air, detached from
// where the hole begins. The leg from the collar to the first shot is drawn
// straight: nothing was measured along it. Not prepended when the first shot
// already sits at the collar, which would only repeat the point. Shared by the
// Charts tab and the Core Orientation tab so the two composites cannot differ.
//
// `prepended` says whether the collar was added, for ChartsTab's collarOffset:
// the repeatability check must not treat it as a measured shot.
export const COLLAR = { depth: 0, northing: 0, easting: 0, tvd: 0, stepout: 0 };

export const withCollarOrigin = (positions) => {
    if (!positions || positions.length === 0) return { lines: [], prepended: false };
    const first = positions[0];
    const atCollar = first.northing === 0 && first.easting === 0 && first.tvd === 0;
    return atCollar
        ? { lines: positions, prepended: false }
        : { lines: [COLLAR, ...positions], prepended: true };
};

// The composite as [east, north, -TVD] points for the Core Orientation 3D chart.
export const compositeTrace = (composite) =>
    // 0 - tvd rather than -tvd, so a point at the collar is 0 and not -0.
    withCollarOrigin(orderedPositions(composite || {})).lines.map(p => [p.easting, p.northing, 0 - p.tvd]);

// The verdict stored on the shot by the server, not one recomputed here: only
// the server knows the axis count and whether the latitude is usable, which is
// why ShotSourceModal renders the stored value too. GTLCore spells it QAPass
// and the AQ path QaPass, so both are read. Null when the shot carries neither.
export const storedQaPass = (orientationSet) => {
    if (!orientationSet) return null;
    const value = orientationSet.QaPass ?? orientationSet.QAPass;
    return typeof value === 'boolean' ? value : null;
};

const finite = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

// Depth ascending. The composite arrives in shot-time order and the sort is
// stable, so runs at the same depth stay earliest first - the same rule the
// composite trajectory uses. Rejected runs (the composite's RejectedShots) are
// listed too, flagged, so the table accounts for every run the 3D view colours
// red - they are no part of the trajectory, but they were drilled.
export const coreRunRows = (composite) => {
    const row = (shot, rejected) => ({
        fileId: shot.SourceFileId,
        surveyName: shot.SourceSurveyName || '',
        depth: finite(shot.Depth),
        inclination: finite(shot.OrientationSet?.Inclination),
        azimuth: finite(shot.OrientationSet?.Azimuth),
        qaPass: storedQaPass(shot.OrientationSet),
        // When the shot was taken - the table's link text, as a survey name is long.
        // Timestamp on CoreTrak/AQ shots; TimeStamp is the MiNS spelling.
        timestamp: shot.Timestamp || shot.TimeStamp || null,
        rejected
    });
    return [
        ...(composite?.SavedShots || []).map(shot => row(shot, false)),
        ...(composite?.RejectedShots || []).map(shot => row(shot, true))
    ]
        .filter(r => r.fileId && r.depth != null)
        .sort((a, b) => a.depth - b.depth);
};
